/**
 * CoverageTasksLiveFeed — manager-only dashboard tile showing the live
 * status of every coverage sub-task on today's Zone Sheet.
 *
 * Header summary line: "X of Y completed · N need acknowledgment · M
 * need attention". Expanded rows list every crew/task pair with their
 * categorized label (Zone - ACOs, Win the Day - Emails, Task - X) and
 * status badge.
 *
 * Polls every 30s. Tapping a task row opens an inline detail dialog
 * with the completion photo (if any) + inline Verify / Send Back /
 * Reassign actions — manager never gets bounced to the Zone Sheet just
 * to verify a single task. (User ask 2026-02-15: "When I try to verify
 * someone else's completed task that has a photo attached, I click
 * into the task and it sends me back to the Home Screen. I never get
 * the option to verify".)
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  ClipboardText, CheckCircle, Warning, CaretRight, Camera, X, CaretDown,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { categorizeTaskLabel, STATUS_META, taskAttributionLine } from "../lib/coverageTasks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sort priority for status — lower number = more urgent. Drives both
// the order of tasks WITHIN a crew card and the order of crew cards
// in the feed (each card uses its highest-priority task). Matches the
// user's spec 2026-06-12: Needs Attention → Needs Handoff → Overdue →
// Awaiting Verify → In Progress → Not Seen → Verified.
const STATUS_PRIORITY = {
  needs_attention: 0,
  needs_handoff: 1,
  completed: 2,         // Awaiting Verify
  in_progress: 3,
  acknowledged: 4,
  not_acknowledged: 5,  // Not Seen
  manager_verified: 6,
};

const todayYmd = () => {
  const now = new Date();
  const mst = new Date(now.toLocaleString("en-US", { timeZone: "America/Denver" }));
  return `${mst.getFullYear()}-${String(mst.getMonth() + 1).padStart(2, "0")}-${String(mst.getDate()).padStart(2, "0")}`;
};

export default function CoverageTasksLiveFeed({ actorId, storeId, onOpenZoneSheet }) {
  const [data, setData] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const date = todayYmd();

  const load = useCallback(async () => {
    if (!actorId) return;
    // Skip while tab is hidden — battery/data saver. (2026-08-01)
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const r = await axios.get(`${API}/zone-sheets/${date}/coverage-tasks/summary`, {
        params: { actor_id: actorId, store_id: storeId || "" },
      });
      setData(r.data);
    } catch {
      setData({ total: 0, totals: {}, items: [] });
    }
  }, [actorId, storeId, date]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    // SSE live push — refetch immediately on any coverage_task_update
    // for our store. Avoids the 30s poll latency so the moment Wendy
    // taps Acknowledge, every manager dashboard at this store updates.
    const onSSE = (e) => {
      const evt = e.detail || {};
      if (evt.kind !== "coverage_task_update") return;
      if (evt.store_id && storeId && evt.store_id !== storeId) return;
      load();
    };
    window.addEventListener("wow:notification", onSSE);
    return () => {
      clearInterval(t);
      window.removeEventListener("wow:notification", onSSE);
    };
  }, [load, storeId]);

  // Deep-link from the notifications bell. HomePage stashes
  // `{task_id, date, store_id}` into sessionStorage when a manager taps
  // a coverage_completion / coverage_handoff notification; we hydrate
  // the corresponding row into `detailTask` so the verify modal opens
  // automatically. (User report 2026-06-12 "manager verification
  // notification is not clickable — should take me directly to the
  // task detail/verification screen".)
  useEffect(() => {
    if (!data || !Array.isArray(data.items) || data.items.length === 0) return;
    let raw = "";
    try { raw = sessionStorage.getItem("wow:openCoverageTask") || ""; } catch { return; }
    if (!raw) return;
    let info = null;
    try { info = JSON.parse(raw); } catch { /* malformed */ }
    if (!info || !info.task_id) return;
    // Notification task_id is "coverage:<uuid>"; the feed items use the
    // bare uuid. Strip the prefix when matching.
    const bareId = String(info.task_id).startsWith("coverage:")
      ? String(info.task_id).slice("coverage:".length)
      : info.task_id;
    const match = data.items.find((it) => it.id === bareId || `coverage:${it.id}` === info.task_id);
    if (match) {
      setDetailTask(match);
      try { sessionStorage.removeItem("wow:openCoverageTask"); } catch { /* ignore */ }
    }
  }, [data]);

  const summary = useMemo(() => {
    if (!data) return null;
    const t = data.totals || {};
    const completed = (t.completed || 0) + (t.manager_verified || 0);
    return {
      total: data.total || 0,
      completed,
      notSeen: t.not_acknowledged || 0,
      acknowledged: t.acknowledged || 0,
      inProgress: t.in_progress || 0,
      awaitingVerify: t.completed || 0,
      verified: t.manager_verified || 0,
      flagged: t.needs_attention || 0,
      handoff: t.needs_handoff || 0,
    };
  }, [data]);

  // Group all tasks by their assigned crew member so the manager sees
  // one card per person (DOM · WENDY · MATTHIAS) instead of one row
  // per task. Cards sort by the most urgent task they own — anyone
  // holding a Needs-Attention or Needs-Handoff task bubbles to the
  // top. Tasks inside each card sort the same way so the action items
  // sit above the "Verified" wins. (User redesign ask 2026-06-12
  // "redesign this section to group tasks by crew member instead of
  // showing every task as its own full row".)
  const crewGroups = useMemo(() => {
    if (!data || !Array.isArray(data.items)) return [];
    const map = new Map();
    for (const it of data.items) {
      const key = it.crew_id || `name:${it.crew_name || "—"}`;
      let g = map.get(key);
      if (!g) {
        g = { crew_id: it.crew_id, crew_name: it.crew_name || "—", shift: it.shift || "", tasks: [] };
        map.set(key, g);
      }
      // Some sources omit shift (zone tasks, checklist items). Pick the
      // first non-empty shift so the card header shows it.
      if (!g.shift && it.shift) g.shift = it.shift;
      g.tasks.push(it);
    }
    const groups = Array.from(map.values()).map((g) => {
      g.tasks.sort((a, b) =>
        (STATUS_PRIORITY[a.status] ?? 99) - (STATUS_PRIORITY[b.status] ?? 99)
      );
      const counts = g.tasks.reduce((acc, it) => {
        acc[it.status] = (acc[it.status] || 0) + 1;
        return acc;
      }, {});
      const verified = counts.manager_verified || 0;
      const awaiting = counts.completed || 0;
      const attention = counts.needs_attention || 0;
      const handoff = counts.needs_handoff || 0;
      const inProg = counts.in_progress || 0;
      const notSeen = (counts.not_acknowledged || 0) + (counts.acknowledged || 0);
      const topPriority = g.tasks.length > 0
        ? (STATUS_PRIORITY[g.tasks[0].status] ?? 99)
        : 99;
      return { ...g, counts, verified, awaiting, attention, handoff, inProg, notSeen, topPriority };
    });
    groups.sort((a, b) => {
      if (a.topPriority !== b.topPriority) return a.topPriority - b.topPriority;
      // Tiebreak: alphabetical so card order is stable across polls.
      return a.crew_name.localeCompare(b.crew_name);
    });
    return groups;
  }, [data]);

  // Auto-expand any crew card that has Needs Attention or Needs Handoff
  // so urgent items aren't hidden behind a tap. Manager can still
  // collapse manually after seeing them.
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      crewGroups.forEach((g) => {
        if ((g.attention > 0 || g.handoff > 0) && next[g.crew_id || g.crew_name] === undefined) {
          next[g.crew_id || g.crew_name] = true;
        }
      });
      return next;
    });
  }, [crewGroups]);

  const toggleCard = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  if (!data || !summary) return null;
  if (summary.total === 0) {
    return (
      <div className="relative rounded-3xl bg-white border border-slate-200/70 shadow-md ring-1 ring-slate-100 overflow-hidden p-4 sm:p-5" data-testid="coverage-feed-empty">
        <div className="absolute inset-x-0 top-0 h-1 pointer-events-none" style={{ background: "linear-gradient(90deg, #10b981 0%, #14b8a6 50%, #06b6d4 100%)" }} />
        <div className="flex items-center gap-2.5 mb-1.5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0" style={{ background: "linear-gradient(135deg, #10b981, #14b8a6)" }}>
            <ClipboardText weight="fill" className="w-4 h-4" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-emerald-600 leading-none">Mission Control</p>
            <h3 className="text-lg font-black text-slate-900 leading-tight mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Today&apos;s Tasks</h3>
          </div>
        </div>
        <p className="text-sm text-slate-500 italic mt-2">No tasks assigned yet — add crew + coverage in the Zone Sheet.</p>
      </div>
    );
  }

  const pct = summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0;

  return (
    <div
      className="relative rounded-3xl bg-white border border-slate-200/70 shadow-md ring-1 ring-slate-100 overflow-hidden"
      data-testid="coverage-feed-card"
    >
      <div className="absolute inset-x-0 top-0 h-1 pointer-events-none" style={{ background: "linear-gradient(90deg, #10b981 0%, #14b8a6 50%, #06b6d4 100%)" }} />
      <div className="p-4 sm:p-5 border-b border-slate-100">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0" style={{ background: "linear-gradient(135deg, #10b981, #14b8a6)" }}>
            <ClipboardText weight="fill" className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-emerald-600 leading-none">Mission Control</p>
            <h3 className="text-lg font-black text-slate-900 leading-tight mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Today&apos;s Tasks</h3>
          </div>
          <span className="ml-auto text-[11px] font-black text-slate-500 tabular-nums">
            {summary.completed}/{summary.total} · {pct}%
          </span>
        </div>
        <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${pct}%`, background: "linear-gradient(90deg, #10b981, #14b8a6)" }}
            data-testid="coverage-feed-progress"
          />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2 text-[10px] font-bold">
          {summary.flagged > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 inline-flex items-center gap-1" data-testid="summary-chip-flagged">
              <Warning weight="fill" className="w-3 h-3" />
              {summary.flagged} Needs Attention
            </span>
          )}
          {summary.handoff > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="summary-chip-handoff">
              {summary.handoff} Needs Handoff
            </span>
          )}
          {summary.awaitingVerify > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="summary-chip-awaiting">
              {summary.awaitingVerify} Awaiting Verify
            </span>
          )}
          {summary.inProgress > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700" data-testid="summary-chip-inprogress">
              {summary.inProgress} In Progress
            </span>
          )}
          {summary.acknowledged > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700" data-testid="summary-chip-ack">
              {summary.acknowledged} Acknowledged
            </span>
          )}
          {summary.notSeen > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700" data-testid="summary-chip-notseen">
              {summary.notSeen} Not Seen
            </span>
          )}
          {summary.verified > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white inline-flex items-center gap-1" data-testid="summary-chip-verified">
              <CheckCircle weight="fill" className="w-3 h-3" />
              {summary.verified} Verified
            </span>
          )}
        </div>
      </div>

      {/* Crew cards — grouped by assignee, priority-sorted. Collapsed by
          default; cards with Needs Attention / Needs Handoff auto-expand
          on first render. Tap header to toggle. Tap a task row inside
          an expanded card to open the verify/sendback modal. */}
      <div className="max-h-[28rem] overflow-y-auto divide-y divide-slate-100">
        {crewGroups.map((g) => {
          const key = g.crew_id || g.crew_name;
          const isOpen = !!expanded[key];
          const total = g.tasks.length;
          const progressDenom = total;
          const progressNum = g.verified;
          // Pick the most urgent banner the card should advertise in
          // its collapsed state — Needs Attention wins, then Handoff,
          // then Awaiting Verify, then In Progress, then Not Seen.
          let collapsedLabel = "";
          if (g.attention > 0) collapsedLabel = `${g.attention} needs attention`;
          else if (g.handoff > 0) collapsedLabel = `${g.handoff} needs handoff`;
          else if (g.awaiting > 0) collapsedLabel = `${g.awaiting} awaiting verify`;
          else if (g.inProg > 0) collapsedLabel = `${g.inProg} in progress`;
          else if (g.notSeen > 0) collapsedLabel = `${g.notSeen} not seen`;
          else collapsedLabel = "All verified";
          const allDone = total > 0 && progressNum === total;
          return (
            <div key={key} data-testid={`crew-card-${key}`}>
              <button
                type="button"
                onClick={() => toggleCard(key)}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50"
                data-testid={`crew-card-toggle-${key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-black text-slate-900 uppercase tracking-wide truncate">
                      {g.crew_name}
                    </p>
                    {g.shift && (
                      <span className="text-[10px] font-bold text-slate-500 whitespace-nowrap">
                        · {g.shift}
                      </span>
                    )}
                    {allDone && (
                      <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-bold text-slate-600">
                      {progressNum}/{progressDenom} verified
                    </span>
                    <span className="text-slate-300 text-[10px]">·</span>
                    <span className={`text-[10px] font-black ${
                      g.attention > 0 ? "text-rose-700"
                      : g.handoff > 0 ? "text-amber-700"
                      : g.awaiting > 0 ? "text-emerald-700"
                      : g.inProg > 0 ? "text-indigo-700"
                      : g.notSeen > 0 ? "text-slate-600"
                      : "text-emerald-700"
                    }`}>
                      {collapsedLabel}
                    </span>
                  </div>
                </div>
                <CaretDown
                  weight="bold"
                  className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isOpen && (
                <div className="bg-slate-50/50 border-t border-slate-100">
                  {g.tasks.map((it) => {
                    const cat = categorizeTaskLabel(it.label);
                    const meta = STATUS_META[it.status] || STATUS_META.not_acknowledged;
                    const sourcePrefix = it.kind === "zone" ? "Zone"
                      : it.kind === "opening" ? "Opening Mgr"
                      : it.kind === "closing" ? "Closing Mgr"
                      : cat.prefix;
                    const sourceLabel = it.kind === "coverage" ? cat.display : it.label;
                    return (
                      <button
                        key={`${it.kind || "coverage"}:${it.task_id}`}
                        type="button"
                        onClick={() => setDetailTask(it)}
                        className="w-full flex items-center gap-2 pl-7 pr-4 py-2 text-left hover:bg-white"
                        data-testid={`coverage-feed-row-${it.task_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-semibold text-slate-800 truncate">
                            <span className="text-slate-500 font-normal">{sourcePrefix} —</span>{" "}
                            {sourceLabel}
                            {it.has_photo && (
                              <span className="inline-flex items-center gap-0.5 ml-1 align-middle">
                                <Camera weight="fill" className="inline w-3 h-3 text-emerald-600" />
                                {it.photo_count > 1 && (
                                  <span className="text-[9px] font-black text-emerald-700">×{it.photo_count}</span>
                                )}
                              </span>
                            )}
                          </p>
                          {it.status === "needs_attention" && it.note && (
                            <p className="text-[10px] text-rose-700 truncate italic">Note: {it.note}</p>
                          )}
                        </div>
                        <span className={`text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded-full border whitespace-nowrap ${meta.chip}`}>
                          {meta.short}
                        </span>
                        <CaretRight weight="bold" className="w-3 h-3 text-slate-300 flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <TaskDetailDialog
        task={detailTask}
        actorId={actorId}
        storeId={storeId}
        date={date}
        onClose={() => setDetailTask(null)}
        onChanged={() => { setDetailTask(null); load(); }}
        onOpenSheet={onOpenZoneSheet}
      />
    </div>
  );
}

/** Inline detail/verify dialog. Opened when manager taps a row in the
 *  live feed — shows full task context (photo, note, completion time)
 *  + inline Verify / Send Back buttons so they never have to leave the
 *  dashboard for a single verification. */
function TaskDetailDialog({ task, actorId, storeId, date, onClose, onChanged, onOpenSheet }) {
  const [busy, setBusy] = useState(false);
  const [fullPhotoIdx, setFullPhotoIdx] = useState(-1);
  const [photoUrls, setPhotoUrls] = useState([]);

  // Lazy-fetch the full task. Coverage tasks live inside
  // crew_assignments[].coverage_tasks; zone-recovery / checklist tasks
  // live at the top level. The summary endpoint only sends has_photo +
  // photo_count (no base64) to keep the live feed light, so when we
  // need the full images we fetch the whole sheet here. Multi-photo
  // tasks return a list at `completion_photos`; legacy single-photo
  // tasks return only `completion_photo` and we wrap it. (User ask
  // 2026-06-13 multi-photo recall.)
  useEffect(() => {
    setPhotoUrls([]);
    setFullPhotoIdx(-1);
    if (!task || !task.has_photo) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/zone-sheets/${date}`, {
          params: { store_id: storeId || "", actor_id: actorId },
        });
        let urls = [];
        if ((task.kind || "coverage") === "coverage") {
          const row = (r.data.crew_assignments || []).find((x) => x.crew_id === task.crew_id);
          const t = (row?.coverage_tasks || []).find((x) => x.id === task.task_id);
          const list = t?.completion_photos || [];
          urls = (Array.isArray(list) ? list : [])
            .map((p) => (typeof p === "string" ? p : p?.url))
            .filter(Boolean);
          if (urls.length === 0 && t?.completion_photo) {
            urls = [t.completion_photo];
          }
        }
        if (!cancelled) setPhotoUrls(urls);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [task, date, storeId, actorId]);

  if (!task) return null;
  const cat = categorizeTaskLabel(task.label);
  const meta = STATUS_META[task.status] || STATUS_META.not_acknowledged;

  const run = async (transition, reassignTargetId) => {
    setBusy(true);
    try {
      const body = { actor_id: actorId, crew_id: task.crew_id, task_id: task.task_id, transition, note: "" };
      if (reassignTargetId) body.reassign_to_crew_id = reassignTargetId;
      await axios.post(`${API}/zone-sheets/${date}/coverage-tasks/transition`, body, { params: { store_id: storeId || "" } });
      onChanged();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't update task");
    } finally {
      setBusy(false);
    }
  };

  const sendBack = async () => {
    // eslint-disable-next-line no-alert
    const note = window.prompt(`Send "${task.label}" back to ${task.crew_name}. What needs fixing?`);
    if (!note || !note.trim()) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/zone-sheets/${date}/coverage-tasks/transition`,
        { actor_id: actorId, crew_id: task.crew_id, task_id: task.task_id, transition: "needs_attention", note: note.trim() },
        { params: { store_id: storeId || "" } },
      );
      onChanged();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't send back");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-black flex items-center gap-2">
            <span className="flex-1">{cat.full}</span>
            <span className={`text-[10px] uppercase tracking-wider font-black px-2 py-0.5 rounded-full border whitespace-nowrap ${meta.chip}`}>
              {meta.short}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Assigned to <strong className="font-black text-slate-700">{task.crew_name}</strong>
            {task.completed_at && (
              <> · Completed {new Date(task.completed_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Attribution — "Verified by Dom" / "Manager Signed Off by Matthias" /
            "Awaiting manager verify". Helps managers reviewing completed
            tasks see at a glance who signed off without opening the
            audit drawer. (User ask 2026-06-13.) */}
        {(() => {
          const byline = taskAttributionLine(task);
          if (!byline) return null;
          const isManagerSelf =
            task.status === "manager_verified"
            && task.verified_by
            && task.verified_by === task.completed_by
            && task.completed_by_is_manager;
          const isAwaiting = task.status === "completed";
          const cls = isAwaiting
            ? "bg-amber-50 border-amber-200 text-amber-800"
            : isManagerSelf
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-emerald-50 border-emerald-200 text-emerald-800";
          return (
            <div
              className={`px-3 py-1.5 border rounded-lg text-[11px] font-bold ${cls}`}
              data-testid="task-detail-attribution"
            >
              {byline}
              {task.verified_at && task.status === "manager_verified" && (
                <span className="font-normal opacity-75"> · {new Date(task.verified_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
              )}
            </div>
          );
        })()}

        {task.note ? (
          <div className="px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700">
            <strong className="font-black">Note:</strong> {task.note}
          </div>
        ) : null}

        {task.has_photo ? (
          photoUrls.length > 0 ? (
            <div data-testid="task-detail-photos">
              {/* Grid for multi-photo recall. Single-photo tasks still
                  render as one big tile because grid-cols-1. */}
              <div className={photoUrls.length === 1 ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-2"}>
                {photoUrls.map((url, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setFullPhotoIdx(i)}
                    className="block w-full rounded-lg overflow-hidden border border-slate-200"
                    data-testid={`task-detail-photo-${i}`}
                  >
                    <img src={url} alt={`Completion ${i + 1}`} className={photoUrls.length === 1 ? "w-full max-h-64 object-cover" : "w-full h-32 object-cover"} />
                  </button>
                ))}
              </div>
              {photoUrls.length > 1 && (
                <p className="mt-1 text-[10px] text-slate-500">{photoUrls.length} photos attached · tap to enlarge</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">Loading photo…</p>
          )
        ) : (
          <p className="text-xs text-slate-500 italic">No completion photo attached.</p>
        )}

        <DialogFooter className="flex-row flex-wrap justify-end gap-2">
          {(task.kind || "coverage") === "coverage" && task.status === "completed" && (
            <button
              type="button"
              onClick={() => run("verify")}
              disabled={busy}
              className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-black inline-flex items-center gap-1"
              data-testid="task-detail-verify"
            >
              <CheckCircle weight="bold" className="w-3.5 h-3.5" /> Verify Complete
            </button>
          )}
          {(task.kind || "coverage") === "coverage" && (task.status === "completed" || task.status === "needs_handoff" || task.status === "manager_verified") && (
            <button
              type="button"
              onClick={sendBack}
              disabled={busy}
              className="px-3 py-2 rounded-lg bg-rose-100 text-rose-700 text-xs font-black"
              data-testid="task-detail-sendback"
            >
              Send Back
            </button>
          )}
          {(task.kind || "coverage") !== "coverage" && (
            <p className="text-[11px] text-slate-500 italic mr-auto">
              {task.kind === "zone" ? "Zone recovery tasks are managed on the Zone Sheet." : "Checklist items are signed off on the Zone Sheet."}
            </p>
          )}
          <button
            type="button"
            onClick={() => { onClose(); onOpenSheet && onOpenSheet(); }}
            className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold"
            data-testid="task-detail-open-sheet"
          >
            Open Zone Sheet
          </button>
        </DialogFooter>
      </DialogContent>

      {/* Full-screen photo viewer with prev/next nav for multi-photo
          tasks. Tap outside the image to close. */}
      {fullPhotoIdx >= 0 && photoUrls[fullPhotoIdx] && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setFullPhotoIdx(-1)}
          data-testid="task-detail-photo-fullscreen"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFullPhotoIdx(-1); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 inline-flex items-center justify-center shadow-lg"
            aria-label="Close photo"
          >
            <X weight="bold" className="w-5 h-5 text-slate-900" />
          </button>
          {photoUrls.length > 1 && fullPhotoIdx > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setFullPhotoIdx(fullPhotoIdx - 1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/85 inline-flex items-center justify-center shadow-lg text-2xl font-black text-slate-900"
              aria-label="Previous photo"
            >‹</button>
          )}
          {photoUrls.length > 1 && fullPhotoIdx < photoUrls.length - 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setFullPhotoIdx(fullPhotoIdx + 1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/85 inline-flex items-center justify-center shadow-lg text-2xl font-black text-slate-900"
              aria-label="Next photo"
            >›</button>
          )}
          <img src={photoUrls[fullPhotoIdx]} alt={`Completion ${fullPhotoIdx + 1}`} className="max-w-full max-h-full object-contain rounded-lg" />
          {photoUrls.length > 1 && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/90 text-[11px] font-black text-slate-700">
              {fullPhotoIdx + 1} / {photoUrls.length}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
